import { Account, ChangeLog, Response, apiAbortController } from '@ui/src';
import {
  BaseTransaction,
  CASP,
} from '@ui/src/components/transactions-sidepanels/types';
import api from '../../../api/api';
import {
  PspPartnerItem,
  PspPartnersValidator,
  PspPartnerFormValues,
  PspAccountsValidator,
  PspAccountItem,
  PspAccountValues,
  PspPartnerLogs,
  PspAccountLogs,
  Balance,
  UploadResponse,
  Document,
  PspIntegration,
  PaymentsSettings,
  ExternalAccount,
  ExternalAccountFormValues,
  IntegrationLog,
  CASPChainFormValues,
  ExternalAccountWithIntegrationFormValues,
  ExternalVaultSettings,
  FeeSettings,
  ExternalAccountTopUpOption,
  PspAccountCategory,
  ExternalAccountCurrenciesSettings,
  TransactionFeeSettings,
  TransactionFeeSettingsFormValues,
  PspPartnersOrganization,
  PspPartnersLinkedClient,
  PspIntegrationOrganization,
  LinkedClientsFormValues,
  ExternalAccountTopUpOptionFormValues,
} from '../types';

export const pspApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPspPartnersValidators: build.query<{ data: PspPartnersValidator }, void>(
      {
        query: () => ({
          url: '/v1/psp-partners/validators',
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getPspAccountsValidators: build.query<{ data: PspAccountsValidator }, void>(
      {
        query: () => ({
          url: '/v1/psp-accounts/validators',
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getConfigurationPspPartners: build.query<Response<PspPartnerItem>, string>({
      query: (query) => ({
        url: `/v1/psp-partners${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPartner: build.query<{ data: PspPartnerItem }, string>({
      query: (id) => ({
        url: `/v1/psp-partners/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationAccountBalance: build.query<Response<Balance>, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspLogs: build.query<Response<PspPartnerLogs>, string>({
      query: (id) => ({
        url: `/v1/psp-logs?pspPartnerId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPspPartner: build.mutation<
      {
        data: PspPartnerItem;
      },
      PspPartnerFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-partners',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspPartner: build.mutation<
      {
        data: PspPartnerItem;
      },
      { body: Partial<PspPartnerFormValues>; id: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    applyPspPartnerIntegration: build.mutation<
      {
        data: PspPartnerItem;
      },
      {
        body: {
          pspIntegrationId: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    disablePspPartner: build.mutation<
      {
        data: PspPartnerItem;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-partners/${id}/disable`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    enablePspPartner: build.mutation<
      {
        data: PspPartnerItem;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-partners/${id}/enable`,
        method: 'PUT',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getConfigurationPspAccounts: build.query<
      Response<PspAccountItem>,
      { id: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/primary-accounts${data.query}`,
      }),
    }),
    createPspAccount: build.mutation<
      {
        data: PspAccountItem;
      },
      PspAccountValues
    >({
      query: (body) => ({
        url: '/v1/psp-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspAccount: build.mutation<
      {
        data: PspAccountItem;
      },
      { body: Partial<PspAccountValues>; id: string }
    >({
      query: (data) => ({
        url: `/v1/psp-accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    enablePspAccount: build.mutation<
      {
        data: PspAccountItem;
      },
      { id: string; active: boolean }
    >({
      query: (data) => ({
        url: `/v1/psp-accounts/${data.id}/active`,
        method: 'PUT',
        body: {
          active: data.active,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspAccount: build.query<{ data: PspAccountItem }, string>({
      query: (id) => ({
        url: `/v1/psp-accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspInternalAccounts: build.query<Response<PspAccountItem>, string>({
      query: (id) => ({
        url: `/v1/psp-accounts?pspPrimaryAccountId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspAccountsLogs: build.query<Response<PspAccountLogs>, string>({
      query: (query) => ({
        url: `/v1/psp-accounts-logs?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    configurationUpload: build.mutation({
      async queryFn(file: File, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('files', file);

        const response = (await fetchWithBQ({
          url: '/v1/uploads',
          method: 'POST',
          body: formData,
        })) as { data: UploadResponse[] };

        return response;
      },
    }),
    addPartnerDocuments: build.mutation<
      {
        data: Document;
      },
      { id: string; body: { uploadId: string; name: string } }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPartnerDocuments: build.query<Response<Document>, string>({
      query: (id) => ({
        url: `/v1/psp-partners/${id}/documents`,
      }),
    }),
    getPspPartnerDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePspPartnerDocument: build.mutation<
      {
        data: { id: string };
      },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/documents/${data.documentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addAccountDocuments: build.mutation<
      {
        data: Document;
      },
      { id: string; body: { uploadId: string; name: string } }
    >({
      query: (data) => ({
        url: `/v1/psp-accounts/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspAccountDocuments: build.query<Response<Document>, string>({
      query: (id) => ({
        url: `/v1/psp-accounts/${id}/documents`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspAccountDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/psp-accounts/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePspAccountDocument: build.mutation<
      {
        data: { id: string };
      },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/psp-accounts/${data.id}/documents/${data.documentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrations: build.query<Response<PspIntegration>, void>({
      query: () => ({
        url: '/v1/psp-integrations',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPaymentsSetting: build.query<Response<PaymentsSettings>, string>({
      query: (pspId) => ({
        url: `/v1/psp-partners/${pspId}/payments-settings`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addPspPaymentsSettings: build.mutation<
      {
        data: PaymentsSettings;
      },
      {
        id: string;
        body: {
          pspIntegrationPaymentId: string;
          bic?: string;
          sortCode?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/payments-settings`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspPaymentsSettings: build.mutation<
      {
        data: PaymentsSettings;
      },
      {
        id: string;
        settingsId: string;
        body: {
          pspPartnerId: string;
          pspIntegrationPaymentId: string;
          bic?: string;
          sortCode?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/payments-settings/${data.settingsId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateVisibleTocClients: build.mutation<
      {
        data: PspPartnerItem;
      },
      {
        id: string;
        body: {
          visibleToClients: boolean;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPSPExternalAccountsList: build.query<Response<ExternalAccount>, string>({
      query: (query) => ({
        url: `/v1/psp-integrations-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPSPExternalAccount: build.query<{ data: ExternalAccount }, string>({
      query: (id) => ({
        url: `/v1/psp-integrations-accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPSPExternalAccount: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindPSPExternalAccount: build.mutation<
      {
        data: {
          ok: boolean;
        };
      },
      { pspAccountId: string }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/bind-psp-account',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    generateNewPSPExternalAccount: build.mutation<
      {
        data: {
          ok: boolean;
        };
      },
      { pspAccountId: string }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/generate-account-for-psp-account',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountsForPspExternal: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspExternalAccountStatus: build.mutation<
      { data: { count: number } },
      { ids: string[]; active: boolean }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/status',
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBankCASP: build.query<Response<CASP>, void>({
      query: () => ({
        url: '/v1/casps?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationsLogs: build.query<
      Response<IntegrationLog>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/logs${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationsLogsById: build.query<
      { data: IntegrationLog },
      { id: string; logId?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/logs/${data.logId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBankChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCaspChains: build.query<
      Response<{ caspId: string; chain: string; id: string }>,
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-casps-chains${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCaspChains: build.mutation<
      {
        data: { caspId: string; chain: string; id: string };
      },
      CASPChainFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-casps-chains',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCaspChains: build.mutation<
      {
        data: ExternalAccount;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations-casps-chains/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPspExternalAccountWithIntegration: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountWithIntegrationFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/generate-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalVaultSettings: build.mutation<
      {
        data: PspIntegration;
      },
      { body: Partial<ExternalVaultSettings>; id: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/external-vault-settings`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExternalAccountAutoCreateCurrenciesSettings: build.mutation<
      {
        data: PspIntegration;
      },
      { body: ExternalAccountCurrenciesSettings; id: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/external-account-auto-create-currencies-settings`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateFeeSettings: build.mutation<
      {
        data: PspIntegration;
      },
      { body: FeeSettings; id: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/fee-settings`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationLogTransactionById: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspExternalAccountTopUpOptions: build.query<
      Response<ExternalAccountTopUpOption>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts?pspIntegrationAccountId=${data.id}&all=true${data.query ? data.query : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspExternalAccountChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspAccountsCategories: build.query<Response<PspAccountCategory>, void>({
      query: () => ({
        url: '/v1/psp-accounts-categories?all=true&active=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationExternalChains: build.query<{ data: string[] }, string>({
      query: (id) => ({
        url: `/v1/psp-integrations/${id}/external-chains`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspExternalAccountCurrencies: build.mutation<
      { data: ExternalAccount },
      { id: string; currenciesIds: string[] }
    >({
      query: (body) => ({
        url: `/v1/psp-integrations-accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationFeeTierList: build.query<
      Response<TransactionFeeSettings>,
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations/${id}/fee-tiers?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPspIntegrationFeeTierList: build.mutation<
      { data: TransactionFeeSettings },
      { id: string; body: TransactionFeeSettingsFormValues }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/fee-tiers`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationFeeTierListItem: build.query<
      { data: TransactionFeeSettings },
      { id: string; itemId: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/fee-tiers/${data.itemId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePspIntegrationFeeTierList: build.mutation<
      { data: TransactionFeeSettings },
      {
        id: string;
        itemId: string;
        body: {
          feeTier: string;
          feeIntegrationAccountId?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/fee-tiers/${data.itemId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePspIntegrationFeeTierList: build.mutation<
      { data: TransactionFeeSettings },
      {
        id: string;
        itemId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/fee-tiers/${data.itemId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPartnerLinkedClients: build.query<
      Response<PspPartnersLinkedClient>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/psp-integrations-organizations${query}&pspPartnerId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPartnersOrganizations: build.query<
      Response<PspPartnersOrganization>,
      string
    >({
      query: (query) => ({
        url: `/v1/clients-organizations?take=40${query ? `&${query}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspPartnerIntegrationsOrganizationsByName: build.query<
      { data: PspIntegrationOrganization[] },
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations/search/external-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindPspPartnerExternalOrganization: build.mutation<
      {
        data: PspPartnersLinkedClient;
      },
      LinkedClientsFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-organizations',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateExchangeToleranceSettings: build.mutation<
      {
        data: PspIntegration;
      },
      {
        body: {
          hasExchangeRateTolerance?: boolean;
          exchangeRateTolerance?: number | null;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations/${data.id}/exchange-tolerance`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deletePSPPaymentAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations-payment-accounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createPSPPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      ExternalAccountTopUpOptionFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-payment-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updatePSPPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      { id: string; body: ExternalAccountTopUpOptionFormValues }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetConfigurationPspPartnersQuery,
  useLazyGetConfigurationPspPartnersQuery,
  useGetPspPartnersValidatorsQuery,
  useLazyGetPspPartnersValidatorsQuery,
  useCreatePspPartnerMutation,
  useLazyGetPspPartnerQuery,
  useUpdatePspPartnerMutation,
  useDisablePspPartnerMutation,
  useEnablePspPartnerMutation,
  useGetPspAccountsValidatorsQuery,
  useLazyGetConfigurationPspAccountsQuery,
  useCreatePspAccountMutation,
  useUpdatePspAccountMutation,
  useEnablePspAccountMutation,
  useLazyGetPspLogsQuery,
  useLazyGetPspAccountQuery,
  useLazyGetPspAccountsLogsQuery,
  useLazyGetPspInternalAccountsQuery,
  useLazyGetConfigurationAccountBalanceQuery,
  useConfigurationUploadMutation,
  useAddPartnerDocumentsMutation,
  useLazyGetPspPartnerDocumentsQuery,
  useLazyGetPspPartnerDocumentFileQuery,
  useDeletePspPartnerDocumentMutation,
  useLazyGetPspAccountDocumentsQuery,
  useAddAccountDocumentsMutation,
  useDeletePspAccountDocumentMutation,
  useLazyGetPspAccountDocumentFileQuery,
  useGetPspIntegrationsQuery,
  useApplyPspPartnerIntegrationMutation,
  useLazyGetPspPaymentsSettingQuery,
  useAddPspPaymentsSettingsMutation,
  useUpdatePspPaymentsSettingsMutation,
  useUpdateVisibleTocClientsMutation,
  useLazyGetPSPExternalAccountsListQuery,
  useCreatePSPExternalAccountMutation,
  useLazyGetAccountsForPspExternalQuery,
  useUpdatePspExternalAccountStatusMutation,
  useLazyGetBankCASPQuery,
  useLazyGetBankChangeLogsQuery,
  useLazyGetPspIntegrationsLogsQuery,
  useLazyGetPspIntegrationsLogsByIdQuery,
  useLazyGetCaspChainsQuery,
  useCreateCaspChainsMutation,
  useDeleteCaspChainsMutation,
  useLazyGetPSPExternalAccountQuery,
  useCreatePspExternalAccountWithIntegrationMutation,
  useUpdateExternalVaultSettingsMutation,
  useUpdateFeeSettingsMutation,
  useLazyGetPspIntegrationLogTransactionByIdQuery,
  useBindPSPExternalAccountMutation,
  useLazyGetPspExternalAccountChangeLogsQuery,
  useLazyGetPspExternalAccountTopUpOptionsQuery,
  useUpdatePspExternalAccountCurrenciesMutation,
  useLazyGetPspAccountsCategoriesQuery,
  useGenerateNewPSPExternalAccountMutation,
  useLazyGetPspIntegrationExternalChainsQuery,
  useUpdateExternalAccountAutoCreateCurrenciesSettingsMutation,
  useLazyGetPspIntegrationFeeTierListQuery,
  useCreatePspIntegrationFeeTierListMutation,
  useLazyGetPspIntegrationFeeTierListItemQuery,
  useUpdatePspIntegrationFeeTierListMutation,
  useDeletePspIntegrationFeeTierListMutation,
  useLazyGetPspPartnerLinkedClientsQuery,
  useLazyGetPspPartnersOrganizationsQuery,
  useLazyGetPspPartnerIntegrationsOrganizationsByNameQuery,
  useBindPspPartnerExternalOrganizationMutation,
  useUpdateExchangeToleranceSettingsMutation,
  useCreatePSPPaymentsAccountsMutation,
  useDeletePSPPaymentAccountsMutation,
  useUpdatePSPPaymentsAccountsMutation,
} = pspApi;

export const {
  endpoints: {
    getConfigurationPspPartners,
    getPspPartnersValidators,
    createPspPartner,
    getPspPartner,
    updatePspPartner,
    disablePspPartner,
    enablePspPartner,
    getPspAccountsValidators,
    getConfigurationPspAccounts,
    createPspAccount,
    updatePspAccount,
    enablePspAccount,
    getPspAccount,
    getPspLogs,
    getPspAccountsLogs,
    getPspInternalAccounts,
    getConfigurationAccountBalance,
    addPartnerDocuments,
    getPspPartnerDocuments,
    getPspPartnerDocumentFile,
    deletePspPartnerDocument,
    getPspAccountDocuments,
    getPspAccountDocumentFile,
    addAccountDocuments,
    deletePspAccountDocument,
    getPspIntegrations,
    applyPspPartnerIntegration,
    getPspPaymentsSetting,
    addPspPaymentsSettings,
    updatePspPaymentsSettings,
    updateVisibleTocClients,
    getPSPExternalAccountsList,
    createPSPExternalAccount,
    getAccountsForPspExternal,
    updatePspExternalAccountStatus,
    getBankCASP,
    getBankChangeLogs,
    getPspIntegrationsLogs,
    getPspIntegrationsLogsById,
    deleteCaspChains,
    getPSPExternalAccount,
    createPspExternalAccountWithIntegration,
    updateExternalVaultSettings,
    updateFeeSettings,
    getPspIntegrationLogTransactionById,
    bindPSPExternalAccount,
    getPspExternalAccountChangeLogs,
    getPspExternalAccountTopUpOptions,
    updatePspExternalAccountCurrencies,
    getPspAccountsCategories,
    generateNewPSPExternalAccount,
    getPspIntegrationExternalChains,
    updateExternalAccountAutoCreateCurrenciesSettings,
    getPspIntegrationFeeTierList,
    createPspIntegrationFeeTierList,
    getPspIntegrationFeeTierListItem,
    updatePspIntegrationFeeTierList,
    deletePspIntegrationFeeTierList,
    getPspPartnerLinkedClients,
    getPspPartnersOrganizations,
    bindPspPartnerExternalOrganization,
    updateExchangeToleranceSettings,
    createPSPPaymentsAccounts,
    updatePSPPaymentsAccounts,
    deletePSPPaymentAccounts,
  },
} = pspApi;
